/**
 * Public marketing homepage (route: "/").
 *
 * Repositioned 2026-07-08 as a free GROWTH PLATFORM (menu + ordering + reviews + loyalty + discovery +
 * intelligence) for cafés, restaurants & bakeries. GTM narrative added 2026-07-09 (Free for Life · pay
 * as you go · in your currency · Born in Germany, growing up in Asia). Internationalised 2026-07-09:
 * every string + content list comes from the `land.*` i18n namespace (en/th/…), with a LangToggle in the
 * nav. Real product screenshots (assets/hero-menu.png, menu-cart.png, assets/shots/*). Login at "/login".
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    FiSmartphone, FiClipboard, FiClock, FiSearch, FiBell, FiZap, FiCheckCircle, FiArrowRight,
    FiShoppingBag, FiStar, FiGift, FiCompass, FiTrendingUp, FiPrinter, FiArrowUp, FiGrid,
} from 'react-icons/fi';
import Logo from '../../assets/brand/cometqr-icon-navy.png';
import deFlag from '../../assets/flags/de.svg';
import heroMenu from '../../assets/hero-menu.png';
import menuCart from '../../assets/menu-cart.png';
import shotDashboard from '../../assets/shots/dashboard.png';
import shotPos from '../../assets/shots/pos.png';
import shotOrders from '../../assets/shots/orders.png';
import shotReports from '../../assets/shots/reports.png';
import { usePageTitle } from '../../components';
import MarketingHeader from '../../components/MarketingHeader';
import './landing.css';

// Icons for the five value pillars + operational essentials (order matches the land.* content arrays).
const PILLAR_ICONS = [<FiShoppingBag />, <FiStar />, <FiGift />, <FiCompass />, <FiTrendingUp />];
const ESSENTIAL_ICONS = [<FiBell />, <FiClipboard />, <FiSearch />, <FiClock />, <FiPrinter />];

const initials = (name) => name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
const asArray = (v) => (Array.isArray(v) ? v : []);

const Landing = () => {
    const { t } = useTranslation();
    usePageTitle('CometQR · Menu, ordering, reviews & loyalty for cafés & restaurants');
    const rootRef = useRef(null);

    // Content lists (localised). returnObjects → arrays/objects; fall back to EN until a locale adds them.
    const venueTypes = asArray(t('land.venue_types', { returnObjects: true }));
    const pillars = asArray(t('land.pillars', { returnObjects: true }));
    const essentials = asArray(t('land.essentials', { returnObjects: true }));
    const pains = asArray(t('land.pains', { returnObjects: true }));
    const venueFits = asArray(t('land.venue_fits', { returnObjects: true }));
    const howSteps = asArray(t('land.how_steps', { returnObjects: true }));
    const need3Models = asArray(t('land.need3_models', { returnObjects: true }));
    const faqs = asArray(t('land.faqs', { returnObjects: true }));

    // Progressive scroll-reveal via IntersectionObserver. Degrades to fully-visible if unsupported.
    useEffect(() => {
        const els = rootRef.current ? rootRef.current.querySelectorAll('.mb-reveal') : [];
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce || !('IntersectionObserver' in window)) { els.forEach(el => el.classList.add('in')); return; }
        const io = new IntersectionObserver((entries) => {
            entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
        }, { threshold: 0.12 });
        els.forEach(el => io.observe(el));
        return () => io.disconnect();
    }, []);

    // Back-to-top button — appears once the guest has scrolled past the hero.
    const [showTop, setShowTop] = useState(false);
    useEffect(() => {
        const onScroll = () => setShowTop(window.scrollY > 600);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Rotating venue-type word in the hero. CONTENT (it tells cafés/bakeries/restaurants they're all
    // welcome), so it rotates regardless of prefers-reduced-motion — the CSS just drops the fade for
    // reduce-motion users. Counter is unbounded and mod'd at render, so a language switch (which changes
    // the list length) can't leave the index out of range.
    const [vt, setVt] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setVt(v => v + 1), 1800);
        return () => clearInterval(id);
    }, []);
    const curVenue = venueTypes.length ? venueTypes[vt % venueTypes.length] : '';

    // Venue-type personaliser (Rajat #5): which "What kind of place do you run?" tab is selected.
    const [fit, setFit] = useState(0);
    const activeFit = venueFits[fit] || venueFits[0] || { key: '', icon: '', line: '', fits: [] };
    const activeFitKey = (activeFit.key || '').toLowerCase();

    // Real, approved testimonials (A3). Empty → the section never renders (no fake social proof).
    const [testimonials, setTestimonials] = useState([]);
    useEffect(() => {
        let alive = true;
        fetch('/api/testimonials/published')
            .then(r => r.json())
            .then(j => {
                if (!alive || !j?.status) return;
                setTestimonials((j.data || []).map(x => ({
                    quote: x.quote, name: x.author_name || x.venue_name || t('land.test_default_name'),
                    role: x.venue_name || '', stars: Math.max(1, Math.min(5, x.rating || 5)),
                })));
            })
            .catch(() => { /* landing still renders without testimonials */ });
        return () => { alive = false; };
    }, [t]);

    return (
        <div className='mb-land' ref={rootRef}>
            {/* NAV — shared marketing header (same on every public page) */}
            <MarketingHeader />

            {/* HERO */}
            <section className='mb-land-hero'>
                <div className='mb-land-wrap mb-land-hero-in'>
                    <div className='mb-land-hero-copy'>
                        <div className='mb-land-eyebrow'>{t('land.hero_eyebrow')}</div>
                        <h1 className='mb-land-h1'>
                            {/* Each part is its own block; the rotating word lives on its OWN nowrap line,
                                so the line count never jumps as the word changes length (Rajat #3). */}
                            <span className='mb-h1-line'>{t('land.hero_h1a')}</span>
                            <span className='mb-h1-line'>{t('land.hero_h1b')}</span>
                            <span className='mb-h1-rotor'><span key={vt} className='accent accent-rotate'>{curVenue}</span>.</span>
                        </h1>
                        <p className='mb-land-lead'>{t('land.hero_lead')}</p>
                        <div className='mb-land-hero-btns'>
                            <Link to='/register' className='mb-btn primary'>{t('land.hero_cta_start')}</Link>
                            <a href='#what' className='mb-btn ghost'>{t('land.hero_cta_see')}</a>
                        </div>
                        <div className='mb-land-microcopy'>{t('land.hero_micro')}</div>
                        <div className='mb-land-origin'>
                            <img src={deFlag} className='mkt-inline-flag' alt='Germany' /> {t('land.hero_origin_de')}
                            <span className='dot'>·</span>
                            <span className='mb-origin-globe' aria-hidden='true'>🌏</span> {t('land.hero_origin_asia')}
                        </div>
                    </div>
                    <div className='mb-land-phone-col'>
                        <div className='mb-land-phone'>
                            <img src={heroMenu} alt='A CometQR menu on a phone, with dish ratings and dietary labels' />
                            <div className='mb-land-phone-badge'><span className='pulse' /> {t('land.hero_badge_kitchen')}</div>
                            <div className='mb-land-phone-chip chip-fav'>{t('land.hero_chip_fav')}</div>
                            <div className='mb-land-phone-chip chip-loy'>{t('land.hero_chip_loy')}</div>
                        </div>
                    </div>
                </div>
                {/* value bar */}
                <div className='mb-land-wrap mb-land-values'>
                    <div className='mb-land-value'><div className='vico'><FiSmartphone /></div><div><b>{t('land.val1_t')}</b><span>{t('land.val1_d')}</span></div></div>
                    <div className='mb-land-value'><div className='vico'><FiZap /></div><div><b>{t('land.val2_t')}</b><span>{t('land.val2_d')}</span></div></div>
                    <div className='mb-land-value'><div className='vico'><FiClipboard /></div><div><b>{t('land.val3_t')}</b><span>{t('land.val3_d')}</span></div></div>
                </div>
            </section>

            {/* WHAT YOU GET — the five pillars */}
            <section className='mb-sec' id='what'>
                <div className='mb-land-wrap'>
                    <div className='mb-sec-head mb-reveal'>
                        <div className='mb-sec-kicker'>{t('land.what_kicker')}</div>
                        <h2 className='mb-sec-title'>{t('land.what_title')}</h2>
                        <p className='mb-sec-sub'>{t('land.what_sub')}</p>
                    </div>
                    <div className='mb-pillars mb-reveal'>
                        {pillars.map((p, i) => (
                            <div className='mb-pillar' key={i}>
                                <div className='pico'>{PILLAR_ICONS[i]}</div>
                                <h3>{p.t}</h3>
                                <p>{p.d}</p>
                            </div>
                        ))}
                    </div>
                    <div className='mb-essentials mb-reveal'>
                        <span className='mb-essentials-label'>{t('land.essentials_label')}</span>
                        {essentials.map((e, i) => (
                            <span className='mb-essential' key={i}>{ESSENTIAL_ICONS[i]} {e}</span>
                        ))}
                    </div>
                </div>
            </section>

            {/* YOUR CONTROL ROOM — real product screenshots */}
            <section className='mb-sec tint' id='control'>
                <div className='mb-land-wrap'>
                    <div className='mb-sec-head center mb-reveal'>
                        <div className='mb-sec-kicker'>{t('land.control_kicker')}</div>
                        <h2 className='mb-sec-title'>{t('land.control_title')}</h2>
                        <p className='mb-sec-sub'>{t('land.control_sub')}</p>
                    </div>
                    <div className='mb-shots mb-reveal'>
                        <figure className='mb-shot wide'>
                            <div className='mb-shot-frame'><span /><span /><span /><img src={shotDashboard} alt='CometQR owner dashboard — orders, revenue and ratings at a glance' /></div>
                            <figcaption><b>{t('land.shot1_b')}</b> {t('land.shot1_r')}</figcaption>
                        </figure>
                        <figure className='mb-shot'>
                            <div className='mb-shot-frame'><span /><span /><span /><img src={shotPos} alt='CometQR point of sale' /></div>
                            <figcaption><b>{t('land.shot2_b')}</b> {t('land.shot2_r')}</figcaption>
                        </figure>
                        <figure className='mb-shot'>
                            <div className='mb-shot-frame'><span /><span /><span /><img src={shotOrders} alt='CometQR live orders board' /></div>
                            <figcaption><b>{t('land.shot3_b')}</b> {t('land.shot3_r')}</figcaption>
                        </figure>
                        <figure className='mb-shot wide'>
                            <div className='mb-shot-frame'><span /><span /><span /><img src={shotReports} alt='CometQR reports and menu intelligence' /></div>
                            <figcaption><b>{t('land.shot4_b')}</b> {t('land.shot4_r')}</figcaption>
                        </figure>
                    </div>
                </div>
            </section>

            {/* WHY OWNERS SWITCH — pain → relief */}
            <section className='mb-sec' id='why'>
                <div className='mb-land-wrap'>
                    <div className='mb-sec-head center mb-reveal'>
                        <div className='mb-sec-kicker'>{t('land.why_kicker')}</div>
                        <h2 className='mb-sec-title'>{t('land.why_title')}</h2>
                        <p className='mb-sec-sub'>{t('land.why_sub')}</p>
                    </div>
                    <div className='mb-pains mb-reveal'>
                        {pains.map((p, i) => (
                            <div className='mb-pain' key={i}>
                                <div className='mb-pain-old'><span className='mb-pain-x'>✕</span><span>{p.pain}</span></div>
                                <div className='mb-pain-new'><FiCheckCircle className='mb-pain-check' /><span>{p.fix}</span></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHAT KIND OF PLACE — venue-type personaliser */}
            <section className='mb-sec tint' id='yourplace'>
                <div className='mb-land-wrap'>
                    <div className='mb-sec-head center mb-reveal'>
                        <div className='mb-sec-kicker'>{t('land.fit_kicker')}</div>
                        <h2 className='mb-sec-title'>{t('land.fit_title')}</h2>
                        <p className='mb-sec-sub'>{t('land.fit_sub')}</p>
                    </div>
                    <div className='mb-fit mb-reveal'>
                        <div className='mb-fit-tabs' role='tablist'>
                            {venueFits.map((v, i) => (
                                <button
                                    key={i}
                                    role='tab'
                                    aria-selected={fit === i}
                                    className={`mb-fit-tab${fit === i ? ' active' : ''}`}
                                    onClick={() => setFit(i)}
                                >
                                    <span className='mb-fit-emoji' aria-hidden='true'>{v.icon}</span>{v.key}
                                </button>
                            ))}
                        </div>
                        <div className='mb-fit-panel' key={fit}>
                            <div className='mb-fit-panel-head'>
                                <span className='mb-fit-panel-emoji' aria-hidden='true'>{activeFit.icon}</span>
                                <div>
                                    <h3>{t('land.fit_made', { venue: activeFitKey })}</h3>
                                    <p>{activeFit.line}</p>
                                </div>
                            </div>
                            <ul className='mb-fit-list'>
                                {asArray(activeFit.fits).map((f, i) => (
                                    <li key={i}><FiCheckCircle /> <span>{f}</span></li>
                                ))}
                            </ul>
                            <Link to='/register' className='mb-btn primary mb-fit-cta'>{t('land.fit_cta', { venue: activeFitKey })}</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS — "live in 5 minutes" onboarding (Rajat #7) */}
            <section className='mb-sec' id='how'>
                <div className='mb-land-wrap'>
                    <div className='mb-sec-head center mb-reveal'>
                        <div className='mb-sec-kicker'>{t('land.how_kicker')}</div>
                        <h2 className='mb-sec-title'>{t('land.how_title')}</h2>
                        <p className='mb-sec-sub'>{t('land.how_sub')}</p>
                    </div>
                    <div className='mb-flow mb-reveal'>
                        {howSteps.map((s, i) => (
                            <div className='mb-flow-step' key={i}><div className='mb-flow-num'>{i + 1}</div><span className='mb-flow-time'>{s.time}</span><h3>{s.h}</h3><p>{s.p}</p></div>
                        ))}
                    </div>
                    <p className='mb-flow-foot mb-reveal'>{t('land.how_foot')}</p>
                </div>
            </section>

            {/* ORDER-AT-TABLE showcase (real screenshot) */}
            <section className='mb-sec tint' id='features'>
                <div className='mb-land-wrap'>
                    <div className='mb-bento mb-reveal' style={{ gridTemplateColumns: '1fr' }}>
                        <div className='mb-cell feature'>
                            <div className='feat-copy'>
                                <div className='cico'><FiCheckCircle /></div>
                                <h3>{t('land.feat_h3')}</h3>
                                <p>{t('land.feat_p')}</p>
                            </div>
                            <img className='feat-shot' src={menuCart} alt='A guest adding a dish to the cart on the CometQR menu' />
                        </div>
                    </div>
                </div>
            </section>

            {/* WHAT YOU NEED — honest hardware requirements (Rajat #6) */}
            <section className='mb-sec' id='need'>
                <div className='mb-land-wrap'>
                    <div className='mb-sec-head center mb-reveal'>
                        <div className='mb-sec-kicker'>{t('land.need_kicker')}</div>
                        <h2 className='mb-sec-title'>{t('land.need_title')}</h2>
                        <p className='mb-sec-sub'>{t('land.need_sub')}</p>
                    </div>
                    <div className='mb-need mb-reveal'>
                        <div className='mb-need-card'>
                            <span className='mb-need-tag have'>{t('land.need1_tag')}</span>
                            <div className='mb-need-ico'><FiSmartphone /></div>
                            <h3>{t('land.need1_h')}</h3>
                            <p>{t('land.need1_p')}</p>
                        </div>
                        <div className='mb-need-card'>
                            <span className='mb-need-tag have'>{t('land.need2_tag')}</span>
                            <div className='mb-need-ico'><FiGrid /></div>
                            <h3>{t('land.need2_h')}</h3>
                            <p>{t('land.need2_p')}</p>
                        </div>
                        <div className='mb-need-card'>
                            <span className='mb-need-tag opt'>{t('land.need3_tag')}</span>
                            <div className='mb-need-ico'><FiPrinter /></div>
                            <h3>{t('land.need3_h')}</h3>
                            <p>{t('land.need3_p')}</p>
                            <div className='mb-need-models'>
                                {need3Models.map((m, i) => <span className='mb-need-model' key={i}>{m}</span>)}
                            </div>
                        </div>
                    </div>
                    <p className='mb-need-note'>{t('land.need_note')}</p>
                </div>
            </section>

            {/* WHY FREE BEATS PAID */}
            <section className='mb-sec tint'>
                <div className='mb-land-wrap'>
                    <div className='mb-why mb-reveal'>
                        <div className='mb-sec-kicker'>{t('land.free_kicker')}</div>
                        <h2 className='mb-sec-title'>{t('land.free_title')}</h2>
                        <p>{t('land.free_p')}</p>
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS — real, approved (A3). Hidden until at least one exists. */}
            {testimonials.length > 0 && (
            <section className='mb-sec' id='testimonials'>
                <div className='mb-land-wrap'>
                    <div className='mb-sec-head center mb-reveal'>
                        <div className='mb-sec-kicker'>{t('land.test_kicker')}</div>
                        <h2 className='mb-sec-title'>{t('land.test_title')}</h2>
                    </div>
                    <div className='mb-tgrid mb-reveal'>
                        {testimonials.map((tm, i) => (
                            <figure className='mb-tcard' key={i}>
                                <div className='mb-tstars' aria-label={`${tm.stars} out of 5 stars`}>{'★'.repeat(tm.stars)}{'☆'.repeat(5 - tm.stars)}</div>
                                <blockquote>“{tm.quote}”</blockquote>
                                <figcaption className='mb-twho'>
                                    <span className='mb-tavatar' aria-hidden='true'>{initials(tm.name)}</span>
                                    <span className='mb-tmeta'><b>{tm.name}</b><span>{tm.role}</span></span>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>
            )}

            {/* PRICING TEASER */}
            <section className='mb-sec' id='pricing'>
                <div className='mb-land-wrap'>
                    <div className='mb-sec-head center mb-reveal'>
                        <div className='mb-sec-kicker'>{t('land.price_kicker')}</div>
                        <h2 className='mb-sec-title'>{t('land.price_title')}</h2>
                    </div>
                    <div className='mb-price-teaser mb-reveal'>
                        <div className='from'>{t('land.price_from')}</div>
                        <div className='big'>€0<span className='per'>{t('land.price_big_per')}</span></div>
                        <div className='mb-price-chips'>
                            <span className='mb-price-chip'><b>{t('land.price_chip1_b')}</b> {t('land.price_chip1_r')}</span>
                            <span className='mb-price-chip'><b>{t('land.price_chip2_b')}</b> {t('land.price_chip2_r')}</span>
                            <span className='mb-price-chip'><b>{t('land.price_chip3_b')}</b> {t('land.price_chip3_r')}</span>
                        </div>
                        <p className='tline'>{t('land.price_tline')}</p>
                        <div className='pbtns'>
                            <Link to='/register' className='mb-btn primary'>{t('land.price_cta_claim')}</Link>
                            <Link to='/pricing' className='mb-btn ghost'>{t('land.price_cta_see')}</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className='mb-sec tint' id='faq'>
                <div className='mb-land-wrap'>
                    <div className='mb-sec-head center mb-reveal'>
                        <div className='mb-sec-kicker'>{t('land.faq_kicker')}</div>
                        <h2 className='mb-sec-title'>{t('land.faq_title')}</h2>
                    </div>
                    <div className='mb-faq mb-reveal'>
                        {faqs.map((f, i) => (
                            <details key={i} open={i === 0}><summary>{f.q}</summary><p>{f.a}</p></details>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className='mb-sec'>
                <div className='mb-land-wrap'>
                    <div className='mb-final mb-reveal'>
                        <h2>{t('land.final_h2')}</h2>
                        <p>{t('land.final_p')}</p>
                        <Link to='/register' className='mb-btn white'>{t('land.final_cta')} <FiArrowRight style={{ verticalAlign: 'middle', marginLeft: 4 }} /></Link>
                        <span className='fine'>{t('land.final_fine')}</span>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className='mb-land-foot'>
                <div className='mb-land-wrap'>
                    <div className='mb-land-foot-in'>
                        <a className='mb-land-foot-brand' href='/'><img src={Logo} alt='CometQR' /></a>
                        <nav className='mb-land-foot-links'>
                            <a href='#what'>{t('land.foot_what')}</a>
                            <a href='#how'>{t('land.foot_how')}</a>
                            <a href='/discover'>{t('land.foot_discover')}</a>
                            <Link to='/pricing'>{t('land.foot_pricing')}</Link>
                            <a href='/login'>{t('land.foot_login')}</a>
                            <a href='/legal/impressum'>{t('land.foot_impressum')}</a>
                            <a href='/legal/privacy'>{t('land.foot_privacy')}</a>
                        </nav>
                    </div>
                    <div className='mb-land-foot-copy'>{t('land.foot_copy')}</div>
                    {/* Brand parentage (2026-07-10 pivot): CometQR ships as a HitCourt product. Proper
                        nouns — deliberately not translated. */}
                    <div className='mb-land-foot-copy' style={{ marginTop: 4 }}>
                        CometQR is a <a href='https://www.hitcourt.com' target='_blank' rel='noreferrer' style={{ color: '#6b7280', fontWeight: 700, textDecoration: 'none' }}>HitCourt</a> product.
                    </div>
                </div>
            </footer>

            {/* Back to top */}
            <button className={`mb-to-top${showTop ? ' show' : ''}`} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label={t('land.back_to_top')}>
                <FiArrowUp />
            </button>
        </div>
    );
};

export default Landing;
